import { defineStore } from 'pinia'
import { useRequest } from 'composables/useRequest.js'
import { consumer } from 'stores/cable.js'
import { playSound } from 'controllers/vue_components/notifications/notificationSounds.js'
import { useGlobalStore } from 'stores/global.js'

const CACHE_TTL = 2 * 60 * 1000 // 2 minutes

export const useNotificationsStore = defineStore('notifications', {
  state: () => ({
    // records and pagination keyed by tab ('all' | 'visits' | 'messages')
    records: { all: [], visits: [], messages: [] },
    subscription: null,
    pageInfo: {
      all:      { current_page: 0, total_pages: 0, total_records: 0 },
      visits:   { current_page: 0, total_pages: 0, total_records: 0 },
      messages: { current_page: 0, total_pages: 0, total_records: 0 },
    },
    fetchedAt: { all: null, visits: null, messages: null },
    loading: false,
    // message detail cache keyed by notification id
    messages: {},        // { [notificationId]: { user_name, email, body, url, sent_at } }
    messageLoading: {},  // { [notificationId]: true/false }
    messageError: {},    // { [notificationId]: true/false }
    // hover card cache keyed by user id
    userCards: {},       // { [userId]: { data, loading, error } }
    // hover card cache keyed by event id
    eventCards: {},      // { [eventId]: { data, loading, error } }
  }),

  getters: {
    isCacheValid: (state) => (tab) => {
      const t = state.fetchedAt[tab]
      return t != null && Date.now() - t < CACHE_TTL
    },
  },

  actions: {
    async fetchIfStale(tab) {
      if (this.isCacheValid(tab) && this.records[tab].length > 0) return
      await this.fetchPage(tab, true)
    },

    async fetchPage(tab, reset = false) {
      if (this.loading) return
      const nextPage = reset ? 1 : this.pageInfo[tab].current_page + 1
      if (!reset && this.pageInfo[tab].current_page > 0 && nextPage > this.pageInfo[tab].total_pages) return

      this.loading = true
      const { axiosRequest } = useRequest()
      const params = { page: { number: nextPage, limit: 15 } }
      if (tab === 'visits')   params.filter = { visits: true }
      if (tab === 'messages') params.filter = { messages: true }

      const resp = await axiosRequest('POST', '/api/v1/notifications', params, false)
      if (!resp.js_error && resp.response?.success) {
        const incoming = resp.response.data.records
        this.records[tab]  = reset ? incoming : [...this.records[tab], ...incoming]
        this.pageInfo[tab] = resp.response.data.page
        if (reset) this.fetchedAt[tab] = Date.now()
        this._autoMarkAsRead(tab, incoming)
      }
      this.loading = false
    },

    async _autoMarkAsRead(tab, incoming) {
      const unreadIds = incoming.filter(n => n.viewed_at === null).map(n => n.id)
      if (unreadIds.length === 0) return
      const { axiosRequest } = useRequest()
      const resp = await axiosRequest('PUT', '/api/v1/notifications/mark_bulk_as_read', { notification: { ids: unreadIds } }, false)
      if (!resp.js_error && resp.response?.success) {
        const now = new Date().toISOString()
        unreadIds.forEach(id => {
          const n = this.records[tab].find(r => r.id === id)
          if (n) n.viewed_at = now
        })
        const g = useGlobalStore()
        g.user.notifications_count = Math.max(0, (g.user.notifications_count || 0) - unreadIds.length)
      }
    },

    async markAllRead(tab) {
      const unreadIds = this.records[tab].filter(n => n.viewed_at === null).map(n => n.id)
      if (unreadIds.length === 0) return
      const { axiosRequest } = useRequest()
      const resp = await axiosRequest('PUT', '/api/v1/notifications/mark_bulk_as_read', { notification: { ids: unreadIds } }, false)
      if (!resp.js_error && resp.response?.success) {
        const now = new Date().toISOString()
        this.records[tab].forEach(n => { if (!n.viewed_at) n.viewed_at = now })
        const g = useGlobalStore()
        g.user.notifications_count = Math.max(0, (g.user.notifications_count || 0) - unreadIds.length)
      }
    },

    async markAsRead(tab, id) {
      const { axiosRequest } = useRequest()
      const resp = await axiosRequest('PUT', `/api/v1/notifications/${id}/mark_as_read`, {}, false)
      if (!resp.js_error && resp.response?.success) {
        const n = this.records[tab].find(r => r.id === id)
        if (n && !n.viewed_at) {
          n.viewed_at = new Date().toISOString()
          const g = useGlobalStore()
          g.user.notifications_count = Math.max(0, (g.user.notifications_count || 0) - 1)
        }
      }
    },

    async markAsUnread(tab, id) {
      const { axiosRequest } = useRequest()
      const resp = await axiosRequest('PUT', `/api/v1/notifications/${id}/mark_as_unread`, {}, false)
      if (!resp.js_error && resp.response?.success) {
        const n = this.records[tab].find(r => r.id === id)
        if (n) {
          n.viewed_at = null
          const g = useGlobalStore()
          g.user.notifications_count = (g.user.notifications_count || 0) + 1
        }
      }
    },

    async deleteNotification(tab, id) {
      const { axiosRequest } = useRequest()
      const resp = await axiosRequest('DELETE', `/api/v1/notifications/${id}`, {}, false)
      if (!resp.js_error && resp.response?.success) {
        this.records[tab] = this.records[tab].filter(r => r.id !== id)
      }
    },

    async connectGoogle() {
      const { axiosRequest } = useRequest()
      const resp = await axiosRequest('POST', '/connected_calendars/google_connect', {}, false)
      if (!resp.js_error && resp.response?.success) window.location.href = resp.response.data.url
    },

    async connectOutlook() {
      const { axiosRequest } = useRequest()
      const resp = await axiosRequest('POST', '/connected_calendars/outlook_connect', {}, false)
      if (!resp.js_error && resp.response?.success) window.location.href = resp.response.data.url
    },

    async fetchMessage(notificationId) {
      if (this.messages[notificationId]) return       // already cached — skip request
      if (this.messageLoading[notificationId]) return // request in flight

      this.messageLoading[notificationId] = true
      this.messageError[notificationId]   = false
      const { axiosRequest } = useRequest()
      const resp = await axiosRequest('GET', `/api/v1/notifications/${notificationId}/message`, {}, false)
      this.messageLoading[notificationId] = false
      if (!resp.js_error && resp.response?.success) {
        this.messages[notificationId] = resp.response.data
      } else {
        this.messageError[notificationId] = true
      }
    },

    async fetchEventCard(eventId) {
      if (!eventId) return
      const cached = this.eventCards[eventId]
      if (cached?.data || cached?.loading) return

      this.eventCards[eventId] = { loading: true, error: false, data: null }
      const { axiosRequest } = useRequest()
      const resp = await axiosRequest('GET', `/api/v1/events/${eventId}/hover_card`, {}, false)
      if (!resp.js_error && resp.response?.success) {
        this.eventCards[eventId] = { loading: false, error: false, data: resp.response.data }
      } else {
        this.eventCards[eventId] = { loading: false, error: true, data: null }
      }
    },

    async fetchUserCard(userId) {
      if (!userId) return
      const cached = this.userCards[userId]
      if (cached?.data || cached?.loading) return   // already cached or in-flight

      this.userCards[userId] = { loading: true, error: false, data: null }
      const { axiosRequest } = useRequest()
      const resp = await axiosRequest('GET', `/api/v1/users/${userId}/hover_card`, {}, false)
      if (!resp.js_error && resp.response?.success) {
        this.userCards[userId] = { loading: false, error: false, data: resp.response.data }
      } else {
        this.userCards[userId] = { loading: false, error: true, data: null }
      }
    },

    invalidate(tab) {
      if (tab) {
        this.fetchedAt[tab] = null
      } else {
        this.fetchedAt = { all: null, visits: null, messages: null }
      }
    },

    subscribeToChannel() {
      if (this.subscription) return
      this.subscription = consumer.subscriptions.create('NotificationsChannel', {
        received: (notification) => {
          this.records.all.unshift(notification)
          if (['received_new_message', 'contact_form_submitted', 'bug_report_submitted'].includes(notification.category)) {
            this.records.messages.unshift(notification)
          }
          const globalStore = useGlobalStore()
          globalStore.user.notifications_count = (globalStore.user.notifications_count || 0) + 1
          const sound  = globalStore.user.notification_sound  ?? 'ding'
          const volume = globalStore.user.notification_volume ?? 80
          playSound(sound, volume)
        },
      })
    },

    unsubscribeFromChannel() {
      this.subscription?.unsubscribe()
      this.subscription = null
    },
  },
})
